/**
 * Fetches and manages quiz configuration data using an optimized pre-fetching strategy.
 * * Initiates the network request immediately upon file parsing to minimize React waterfall delays.
 * Identifies the quiz using a `quizId` URL parameter (for decoupled/headless architectures)
 * or falls back to `postId` and `apiUrl` provided via the global `window` object (WordPress integration).
 * * Enhances the base render-as-you-fetch pattern by safely handling client-side routing changes
 * and exposing a `refetch` method to recover from network failures (preventing dead promises).
 * * @see Back-end logic: `submodules/commercecore-ecom/src/CcRestApi/Controllers/QuizPageController.php`
 * @returns {{ data: QuizConfig | null, isLoading: boolean, error: string | null, refetch: () => Promise<void> }} An object containing the quiz configuration data, loading state, error state, and a refetch function.
 */

import { useState, useEffect, useCallback } from "react";
import type { QuizConfig } from "@/types/api.types";

declare global {
  interface Window {
    postId: number;
    apiUrl: string;
    ccRestAccess?: {
      csrfToken?: string;
    };
  }
}

const fetchQuiz = async (
  quizId: string | number,
  apiUrl: string,
): Promise<QuizConfig> => {
  const baseUrl = apiUrl.endsWith("/") ? apiUrl : `${apiUrl}/`;
  const fullUrl = `${baseUrl}api/v2/quiz-page-dto/${quizId}`;

  const res = await fetch(fullUrl, {
    credentials: "same-origin",
    headers: {
      "X-CC-CSRF-Token": window.ccRestAccess?.csrfToken ?? "",
    },
  });
  if (!res.ok) {
    throw new Error(`Server returned ${res.status} for ${fullUrl}`);
  }
  return res.json();
};

let preloadedDataPromise: Promise<QuizConfig> | null = null;
let initialQuizId: string | number | null = null;

if (typeof window !== "undefined") {
  const urlParams = new URLSearchParams(window.location.search);
  initialQuizId = urlParams.get("quizId") || window.postId;

  if (initialQuizId && window.apiUrl) {
    preloadedDataPromise = fetchQuiz(initialQuizId, window.apiUrl);
  } else {
    console.error("Missing quizId or apiUrl to start pre-fetching.");
  }
}

export const useQuizData = () => {
  const [data, setData] = useState<QuizConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(async (forceRefetch = false) => {
    if (typeof window === "undefined") return;

    setIsLoading(true);
    setError(null);

    const urlParams = new URLSearchParams(window.location.search);
    const currentQuizId = urlParams.get("quizId") || window.postId;

    try {
      if (
        !forceRefetch &&
        preloadedDataPromise &&
        String(currentQuizId) === String(initialQuizId)
      ) {
        const jsonData = await preloadedDataPromise;
        setData(jsonData);
      } else if (currentQuizId && window.apiUrl) {
        const jsonData = await fetchQuiz(currentQuizId, window.apiUrl);
        setData(jsonData);
      } else {
        setError("Missing quizId or apiUrl");
      }
    } catch (err) {
      console.error("API Fetch Error:", err);
      setError(err instanceof Error ? err.message : "Failed to load quiz");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  return { data, isLoading, error, refetch: () => loadData(true) };
};
